import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Printer } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export const CGVDocument: React.FC = () => {
  const documentRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!documentRef.current || isGenerating) return;
    
    setIsGenerating(true);
    
    try {
      const element = documentRef.current;
      
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1200,
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: 'a4',
        hotfixes: ['px_scaling']
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 0;
      const totalPages = Math.ceil(imgHeight / pdfHeight);

      while (heightLeft > 0) {
        if (pageNumber > 0) {
          pdf.addPage();
        }
        
        pdf.addImage(
          canvas.toDataURL('image/png'),
          'PNG',
          0,
          position,
          imgWidth,
          imgHeight,
          undefined,
          'FAST'
        );
        
        // Ajouter numéro de page
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `Page ${pageNumber + 1} / ${totalPages}`,
          pdfWidth / 2,
          pdfHeight - 20,
          { align: 'center' }
        );
        
        heightLeft -= pdfHeight;
        position -= pdfHeight;
        pageNumber++;
      }

      pdf.save('CGPS_STRATOS_Hospitality_2025.pdf');
      
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Actions */}
      <Card className="p-4 print:hidden">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            Conditions Générales de Prestations de Services (CGPS)
          </h2>
          <div className="flex gap-3">
            <Button onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimer
            </Button>
            <Button variant="outline" onClick={handleDownloadPDF} disabled={isGenerating}>
              <Download className="w-4 h-4 mr-2" />
              {isGenerating ? 'Génération...' : 'Télécharger PDF'}
            </Button>
          </div>
        </div>
        {isGenerating && (
          <p className="text-sm text-blue-600 mt-3 animate-pulse">
            ⏳ Génération du PDF en cours... Veuillez patienter.
          </p>
        )}
      </Card>

      {/* CGV Content */}
      <div ref={documentRef}>
      <Card className="p-12 bg-white print:shadow-none print:border-0 max-w-5xl mx-auto">
        {/* Page de garde */}
        <div className="text-center mb-12 pb-8 border-b-2 border-stratos-gold">
          <h1 
            className="text-5xl font-bold mb-2"
            style={{
              background: 'linear-gradient(to right, #E9BC66, #F4D89E)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            STRATOS HOSPITALITY
          </h1>
          <p className="text-sm text-gray-500 mb-8">by Getup Agency</p>
          
          <h2 className="text-3xl font-bold text-gray-900 mt-8 mb-4">
            CONTRAT CADRE ET CONDITIONS DE PRESTATION
          </h2>
          <p className="text-xl text-gray-600 mb-8">2025</p>
          
          <p style={{
            textAlign: 'center',
            margin: '30px auto',
            padding: '18px 40px',
            border: '4px solid #000000',
            backgroundColor: '#FFFFFF',
            fontSize: '13px',
            fontWeight: 'bold',
            color: '#000000',
            lineHeight: '1.6',
            maxWidth: '600px',
            display: 'block'
          }}>
            ⚠ DOCUMENT CONFIDENTIEL ⚠
            <br />
            <span style={{ fontSize: '11px', fontWeight: '600' }}>
              Réservé aux partenaires et clients de STRATOS Hospitality
            </span>
          </p>
        </div>

        {/* Informations STRATOS */}
        <div className="mb-12 p-6 bg-gray-50 rounded-lg">
          <h3 className="text-sm font-bold text-stratos-gold uppercase mb-3">
            Informations Légales
          </h3>
          <div className="text-sm text-gray-700 space-y-1">
            <p><strong>Raison sociale :</strong> STRATOS Hospitality (by Getup Agency)</p>
            <p><strong>Forme juridique :</strong> SARL au capital de 150 500€</p>
            <p><strong>Siège social :</strong> 10 impasse de l'Olivaie, 83210 La Farlède</p>
            <p><strong>SIRET :</strong> 822 096 913 00022</p>
            <p><strong>TVA Intracommunautaire :</strong> FR82822096913</p>
            <p><strong>Représentée par :</strong> Adrien Cerdan</p>
            <p><strong>Email de contact :</strong> contact@stratos-hospitality.com</p>
            <p><strong>Site web :</strong> www.stratos-hospitality.com</p>
          </div>
        </div>

        {/* Préambule */}
        <div className="mb-8 p-6 bg-blue-50 border-l-4 border-blue-600 print:break-inside-avoid">
          <h3 className="text-lg font-bold text-gray-900 mb-3">
            PRÉAMBULE – Clause d'acceptation préalable
          </h3>
          <p className="text-sm text-gray-700 leading-relaxed mb-3">
            La signature du devis, de l'ordre de mission ou du présent contrat vaut <strong>acceptation 
            intégrale</strong> des présentes Conditions Générales de Prestations de Services STRATOS 
            Hospitality et de leurs annexes (RGPD, Fidélité, Parrainage).
          </p>
          <p className="text-sm text-gray-700 leading-relaxed">
            Les Parties reconnaissent avoir lu, compris et accepté ces conditions avant tout engagement.
          </p>
        </div>

        {/* Articles */}
        <div className="space-y-8 text-sm text-gray-700 leading-relaxed">
          
          {/* Article 1 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 1 – Champ d'application
            </h3>
            <p className="mb-3">
              Les présentes Conditions Générales de Prestations de Services (CGPS) s'appliquent à toutes 
              les prestations réalisées par <strong>STRATOS Hospitality</strong> (ci-après "STRATOS Hospitality") 
              pour ses clients professionnels (ci-après "le Client").
            </p>
            <p>
              La signature du devis, de l'ordre de mission ou du présent contrat vaut acceptation pleine 
              et entière des présentes CGPS, qui prévalent sur tout document du Client, sauf accord écrit contraire.
            </p>
          </section>

          {/* Article 2 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 2 – Objet de la prestation
            </h3>
            <p className="mb-3">
              STRATOS Hospitality réalise des missions de conseil, d'audit, de développement et 
              d'accompagnement stratégique selon une <strong>obligation de moyens</strong>.
            </p>
            <p className="font-semibold text-gray-900">
              Aucun résultat commercial, financier ou digital n'est garanti.
            </p>
          </section>

          {/* Article 3 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 3 – Obligations du Client
            </h3>
            <p className="mb-3">Le Client s'engage à :</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Collaborer activement et fournir les informations nécessaires</li>
              <li>Répondre sous 5 jours ouvrés à toute demande utile à la mission</li>
              <li>Assumer les conséquences d'un retard (décalage de calendrier ou frais supplémentaires)</li>
            </ul>
          </section>

          {/* Article 4 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 4 – Durée et calendrier
            </h3>
            <p className="mb-3">
              Le contrat prend effet à la signature du devis ou de l'ordre de mission.
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Audit 360° Complet :</strong> mission ponctuelle se terminant à la livraison.
              </li>
              <li>
                <strong>Formules Standard, Premium et Signature :</strong> engagement ferme de six (6) mois 
                minimum, reconductible par tacite reconduction par périodes de six (6) mois, sauf dénonciation 
                écrite par l'une des parties au moins trente (30) jours avant l'échéance.
              </li>
            </ul>
          </section>

          {/* Article 5 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 5 – Prix et conditions de paiement
            </h3>
            
            <h4 className="font-semibold text-gray-900 mb-2">5.1 Prestations ponctuellesutilisation</h4>
            <p className="mb-3">
              L'Audit 360° Complet est facturé <strong>4 000 € HT</strong>, payable 30 % à la signature, 
              solde à la livraison.
            </p>

            <h4 className="font-semibold text-gray-900 mb-2">5.2 Prestations récurrentes</h4>
            <p className="mb-3">
              Les formules Standard, Premium et Signature sont facturées mensuellement à terme à échoir, 
              par <strong>prélèvement SEPA automatique</strong>.
            </p>
            <p className="mb-3">
              Le Client choisit un engagement ferme de 6 mois ou 12 mois :
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
              <li><strong>Engagement 6 mois :</strong> paiement immédiat du premier mois à la signature</li>
              <li><strong>Engagement 12 mois :</strong> paiement immédiat des deux premiers mois à la signature</li>
            </ul>
            <p className="mb-3">
              Le Client bénéficie d'un <strong>avantage fidélité de 15 %</strong> sur le total annuel en 
              cas d'engagement ferme de 12 mois.
            </p>
            <p>
              En cas de résiliation anticipée, les sommes dues jusqu'à la fin de la période d'engagement 
              restent exigibles.
            </p>

            <h4 className="font-semibold text-gray-900 mb-2 mt-4">5.3 Retards de paiement</h4>
            <p className="mb-2">Tout retard entraîne, sans mise en demeure préalable :</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Des pénalités calculées au taux directeur de la Banque Centrale Européenne (BCE) majoré de 10 points</li>
              <li>Une indemnité forfaitaire de 40 € pour frais de recouvrement</li>
            </ul>
            <p className="mt-2">
              STRATOS Hospitality peut suspendre immédiatement la mission jusqu'à régularisation.
            </p>
          </section>

          {/* Article 6 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 6 – Propriété intellectuelle
            </h3>
            <p className="mb-3">
              STRATOS Hospitality conserve la propriété de ses outils, modèles, méthodes et savoir-faire 
              ("Background STRATOS Hospitality").
            </p>
            <p className="mb-3">
              Les livrables spécifiques sont concédés au Client sous licence perpétuelle, non exclusive, 
              non transférable, limitée à son secteur d'activité.
            </p>
            <p>
              Toute réutilisation ou cession à un tiers requiert accord écrit de STRATOS Hospitality.
            </p>
          </section>

          {/* Article 7 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 7 – Confidentialité
            </h3>
            <p className="mb-3">
              Les Parties s'engagent à garder confidentielles toutes les informations échangées pendant 
              <strong> cinq (5) ans</strong> après la mission.
            </p>
            <p>
              STRATOS Hospitality peut citer le Client (nom, logo, secteur) comme référence sauf opposition 
              écrite sous 15 jours.
            </p>
          </section>

          {/* Article 8 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 8 – Données personnelles (RGPD)
            </h3>
            <p className="mb-3">
              STRATOS Hospitality agit en qualité de <strong>sous-traitant</strong>, le Client en qualité 
              de <strong>responsable de traitement</strong>.
            </p>
            <p className="mb-3">
              Les engagements RGPD figurent dans l'<strong>Annexe 1</strong>.
            </p>
            <p>
              STRATOS Hospitality ne peut être tenue responsable des interruptions liées à des outils tiers.
            </p>
          </section>

          {/* Article 9 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 9 – Responsabilité
            </h3>
            <p className="mb-3">
              STRATOS Hospitality est tenue à une <strong>obligation de moyens</strong>.
            </p>
            <p className="mb-3">
              Sa responsabilité est limitée au <strong>montant HT du contrat</strong>.
            </p>
            <p className="mb-3">
              Ce plafond ne s'applique pas en cas de faute lourde, fraude ou manquement à la confidentialité.
            </p>
            <p>
              Aucun dommage indirect ou perte de profit ne pourra être réclamé.
            </p>
          </section>

          {/* Article 10 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 10 – Réputation, communication et références
            </h3>
            <p className="mb-3">
              Le Client s'interdit toute communication pouvant nuire à STRATOS Hospitality.
            </p>
            <p>
              STRATOS Hospitality peut mentionner le nom, le logo et le secteur d'activité du Client à 
              titre de référence, avec accord écrit préalable.
            </p>
          </section>

          {/* Article 11 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 11 – Résiliation et suspension
            </h3>
            <p className="mb-3">
              Chaque partie peut résilier le contrat après une mise en demeure restée infructueuse pendant 
              quinze (15) jours.
            </p>
            <p>
              STRATOS Hospitality peut suspendre la mission en cas de non-paiement ou d'atteinte à son image.
            </p>
          </section>

          {/* Article 12 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 12 – Force majeure
            </h3>
            <p className="mb-3">
              Aucune Partie ne sera responsable d'un manquement résultant d'un cas de force majeure 
              (pandémie, guerre, grève, cyberattaque, etc.).
            </p>
            <p>
              Si la situation perdure plus de trente (30) jours, le contrat peut être résilié sans indemnité.
            </p>
          </section>

          {/* Article 13 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 13 – Non-sollicitation
            </h3>
            <p>
              Le Client s'interdit d'embaucher un collaborateur STRATOS Hospitality pendant la mission et 
              12 mois après sa fin, sous peine d'une indemnité égale à <strong>6 mois de salaire brut</strong>.
            </p>
          </section>

          {/* Article 14 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 14 – Cession et sous-traitance
            </h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>STRATOS Hospitality peut sous-traiter tout ou partie de la mission sous sa responsabilité</li>
              <li>Le Client ne peut céder le contrat sans accord écrit de STRATOS Hospitality</li>
            </ul>
          </section>

          {/* Article 15 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 15 – Annulation tardive
            </h3>
            <p>
              Toute annulation moins de 15 jours avant le démarrage entraîne une indemnité de <strong>20 % 
              du montant total du devis</strong>, en sus de l'acompte.
            </p>
          </section>

          {/* Article 16 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 16 – Conservation des livrables
            </h3>
            <p className="mb-3">
              STRATOS Hospitality conserve les livrables pendant <strong>12 mois</strong> après la livraison finale.
            </p>
            <p>
              Au-delà, elle n'a plus d'obligation d'archivage.
            </p>
          </section>

          {/* Article 17 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 17 – Programme de parrainage STRATOS Hospitality
            </h3>
            <p className="mb-3">
              Tout Client STRATOS Hospitality actif peut recommander un nouveau client ("Filleul").
            </p>
            
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-3">
              <p className="font-semibold text-green-900 mb-2">Avantage Parrainage :</p>
              <ul className="list-disc list-inside space-y-1 ml-2 text-green-900">
                <li>Contrat du Filleul = crédit de <strong>5 % HT</strong> sur le montant signé</li>
                <li>Si le contrat du Parrain est prolongé à 12 mois, le crédit passe automatiquement à 
                <strong> 10 % HT</strong> pour les Filleuls concernés</li>
              </ul>
            </div>

            <p className="mb-3">
              Les crédits s'appliquent sur les prochaines factures du Parrain, non remboursables, valables 
              pendant la durée du contrat et au maximum douze (12) mois à compter de leur attribution.
            </p>
            <p>
              À l'issue du contrat, qu'il soit semestriel ou annuel, le crédit de parrainage est 
              automatiquement remis à zéro.
            </p>
          </section>

          {/* Article 18 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 18 – Médiation et litiges
            </h3>
            <p className="mb-3">
              Tout différend sera soumis à une <strong>médiation CMAP</strong> (30 jours) avant recours judiciaire.
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li><strong>Compétence exclusive :</strong> Tribunaux de Paris</li>
              <li><strong>Droit applicable :</strong> droit français</li>
            </ul>
          </section>

          {/* Article 19 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 19 – Intégralité contractuelle
            </h3>
            <p className="mb-3">
              Le devis, l'ordre de mission et les présentes CGPS forment un ensemble contractuel unique.
            </p>
            <p>
              Toute condition d'achat du Client est réputée non écrite sauf accord express de STRATOS Hospitality.
            </p>
          </section>

          {/* Article 20 */}
          <section className="print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ARTICLE 20 – Signature et consentement
            </h3>
            <p className="mb-6">
              Signé électroniquement, le présent document vaut consentement contractuel intégral au sens 
              de l'article 1367 du Code civil.
            </p>

            <div className="grid grid-cols-2 gap-8 pt-8 border-t-2 border-stratos-gold">
              <div>
                <p className="text-xs text-gray-600 mb-2">Fait à ____________________</p>
                <p className="text-xs text-gray-600 mb-8">Le ____ / ____ / 2025</p>
                
                <p className="font-bold text-gray-900 mb-2">Le Client</p>
                <p className="text-xs text-gray-600 mb-1">Nom / Raison sociale :</p>
                <div className="border-b border-gray-300 mb-6 pb-1">_______________________</div>
                <p className="text-xs text-gray-600 mb-12">Signature :</p>
              </div>

              <div>
                <p className="text-xs text-gray-600 mb-2">Fait à ____________________</p>
                <p className="text-xs text-gray-600 mb-8">Le ____ / ____ / 2025</p>
                
                <p className="font-bold text-gray-900 mb-2">STRATOS Hospitality</p>
                <p className="text-xs text-gray-600 mb-1">Représentée par :</p>
                <div className="border-b border-gray-300 mb-6 pb-1">Adrien Cerdan</div>
                <p className="text-xs text-gray-600 mb-12">Signature :</p>
              </div>
            </div>
          </section>
        </div>

        {/* Footer avec mentions légales */}
          <div className="mt-12 pt-6 border-t text-center text-xs text-gray-500 space-y-1">
            <p>
              <strong>STRATOS Hospitality</strong> (by Getup Agency) – SARL au capital de 150 500€
            </p>
            <p>10 impasse de l'Olivaie, 83210 La Farlède</p>
            <p>SIRET : 822 096 913 00022 – TVA : FR82822096913</p>
            <p>Représentée par Adrien Cerdan</p>
            <p className="mt-3">www.stratos-hospitality.com – contact@stratos-hospitality.com</p>
            <p className="mt-2">© STRATOS Hospitality 2025 – Tous droits réservés</p>
          </div>
      </Card>
      </div>
    </div>
  );
};
